/**
 * components/ios-install-banner.tsx
 *
 * Bottom-anchored banner shown on iOS Safari when the app is not yet installed.
 * Instructs the user to tap the Share icon → "Add to Home Screen".
 * Dismisses permanently (localStorage) on close.
 */
import { useIosInstallPrompt } from '../hooks/use-ios-install-prompt'

export default function IosInstallBanner() {
  const { show, dismiss } = useIosInstallPrompt()

  if (!show) return null

  return (
    <>
      {/* Backdrop — tapping it dismisses */}
      <div
        className="fixed inset-0 z-[90] bg-black/20 animate-fade-in"
        onClick={dismiss}
        aria-hidden="true"
      />

      {/* Banner */}
      <div
        className="
          fixed bottom-0 left-0 right-0 z-[91]
          max-w-[430px] mx-auto
          bg-white rounded-t-[24px]
          shadow-[0_-4px_32px_rgba(44,24,16,0.12)]
          px-5 pt-5 pb-[calc(env(safe-area-inset-bottom)+20px)]
          animate-slide-up
        "
        role="dialog"
        aria-label="Add Just Us to your home screen"
      >
        {/* Drag handle */}
        <div className="w-[36px] h-[4px] rounded-full bg-[#F4C0D1] mx-auto mb-5" />

        {/* App icon + text */}
        <div className="flex items-center gap-4 mb-5">
          <img
            src="/apple-touch-icon.png"
            alt="Just Us icon"
            className="w-[56px] h-[56px] rounded-[14px] shadow-sm flex-shrink-0"
          />
          <div>
            <p className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810] leading-tight">
              Add to Home Screen
            </p>
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] mt-0.5">
              Get the full Just Us experience
            </p>
          </div>
        </div>

        {/* Instruction steps */}
        <div className="bg-[#FAF8F4] rounded-[16px] p-4 flex flex-col gap-3 mb-5">
          <Step n={1}>
            Tap the{' '}
            <span className="inline-flex items-center gap-1">
              <ShareIcon />
              <span className="font-medium text-[#2C1810]">Share</span>
            </span>{' '}
            button at the bottom of Safari
          </Step>
          <div className="h-px bg-[#F4C0D1]" />
          <Step n={2}>
            Scroll down and tap{' '}
            <span className="font-medium text-[#2C1810]">"Add to Home Screen"</span>
          </Step>
          <div className="h-px bg-[#F4C0D1]" />
          <Step n={3}>
            Tap <span className="font-medium text-[#2C1810]">"Add"</span> in the top-right corner
          </Step>
        </div>

        {/* Dismiss */}
        <button
          type="button"
          onClick={dismiss}
          className="
            w-full h-[48px] rounded-[14px]
            border border-[#F4C0D1]
            font-['DM_Sans'] text-[14px] text-[#B89AA2]
            active:opacity-60 transition-opacity duration-100
            touch-manipulation
          "
        >
          Maybe later
        </button>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="
        w-[22px] h-[22px] rounded-full
        bg-[#D4537E] text-white
        font-['DM_Sans'] text-[11px] font-bold
        flex items-center justify-center flex-shrink-0 mt-0.5
      ">
        {n}
      </span>
      <p className="font-['DM_Sans'] text-[13px] text-[#7A5A62] leading-snug">
        {children}
      </p>
    </div>
  )
}

// iOS Safari share icon (the box-with-arrow-up shape)
function ShareIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#D4537E"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="inline-block"
    >
      <path d="M8 6H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-2" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  )
}
